import { useState, useEffect, useRef } from 'react';
import { uploadAPI, facilitatorAPI } from '../lib/api';
import { Button } from './ui/button';
import { Upload, CheckCircle2, AlertCircle, Loader2, FileText, X, Clock, XCircle } from 'lucide-react';
import DocumentHistory from './DocumentHistory';

const STATUS_BADGE = {
  pending: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-800 border-green-200', Icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-200', Icon: XCircle },
};

const DocStatusBadge = ({ status }) => {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.cls}`} data-testid={`doc-status-${status || 'pending'}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const DOC_TYPES = [
  { key: 'id_document', label: 'ID Document', multiple: false, note: null },
  { key: 'police_clearance', label: 'Police Clearance Certificate', multiple: false, note: null },
  { key: 'qualification', label: 'Qualifications / Certifications', multiple: true, note: 'You may upload more than one qualification' },
];

const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024;

const UploadSlot = ({ type, docs, disabled, onUpload, onRemove }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const replaceIdRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const replaceId = replaceIdRef.current;
    replaceIdRef.current = null;
    if (!file) return;
    setError('');

    if (!ALLOWED.includes(file.type)) {
      setError(`"${file.name}" is not a supported format. Upload a PDF, JPG or PNG.`);
      return;
    }
    if (file.size > MAX_SIZE) {
      setError(`"${file.name}" is larger than 10MB. Please upload a smaller file.`);
      return;
    }

    setUploading(true);
    try {
      const res = await uploadAPI.document(file, type.key, type.label, !!replaceId, replaceId);
      // Backend removes the replaced rejected doc; reflect that locally, then add the new one
      if (replaceId) onRemove(replaceId, { localOnly: true });
      onUpload(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : `Failed to upload "${file.name}". Please try again.`);
    }
    setUploading(false);
  };

  const openPicker = (replaceId = null) => {
    replaceIdRef.current = replaceId;
    inputRef.current?.click();
  };

  const showUploadButton = type.multiple || docs.length === 0;

  return (
    <div className="border border-border rounded-xl p-4" data-testid={`doc-slot-${type.key}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <p className="font-medium text-sm text-foreground">{type.label}</p>
          <p className="text-xs text-muted-foreground">PDF, JPG, PNG · Max 10MB</p>
          {type.note && <p className="text-xs text-muted-foreground mt-0.5">{type.note}</p>}
        </div>
      </div>

      {/* Uploaded files */}
      {docs.length > 0 && (
        <ul className="space-y-2 mt-3">
          {docs.map((doc) => {
            const status = doc.review_status || 'pending';
            const rowCls = status === 'approved'
              ? 'bg-green-50 border-green-200'
              : status === 'rejected'
              ? 'bg-red-50 border-red-200'
              : 'bg-muted/40 border-border';
            return (
              <li
                key={doc.id}
                className={`rounded-lg border px-3 py-2 ${rowCls}`}
                data-testid={`doc-uploaded-${doc.id}`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {doc.original_filename || doc.filename}
                    </p>
                    <div className="mt-1"><DocStatusBadge status={status} /></div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(doc.id)}
                    className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 shrink-0 self-start"
                    data-testid={`doc-remove-${doc.id}`}
                  >
                    <X className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
                {status === 'rejected' && doc.rejection_reason && (
                  <p className="text-xs text-red-700 mt-2 pl-6" data-testid={`doc-rejection-reason-${doc.id}`}>
                    <span className="font-medium">Reason:</span> {doc.rejection_reason}
                  </p>
                )}
                {status === 'rejected' && (
                  <div className="mt-2 pl-6">
                    <Button
                      type="button"
                      size="sm"
                      disabled={disabled || uploading}
                      onClick={() => openPicker(doc.id)}
                      className="rounded-full h-8 bg-forest hover:bg-forest/90 text-white"
                      data-testid={`doc-replace-btn-${doc.id}`}
                    >
                      {uploading ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>
                      ) : (
                        <><Upload className="w-3.5 h-3.5 mr-1.5" /> Replace document</>
                      )}
                    </Button>
                  </div>
                )}
                <DocumentHistory history={doc.history} testIdPrefix={`doc-${doc.id}`} />
              </li>
            );
          })}
        </ul>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 mt-3 text-destructive text-sm" data-testid={`doc-error-${type.key}`}>
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <input
        type="file"
        ref={inputRef}
        onChange={handleChange}
        accept="application/pdf,image/jpeg,image/png"
        className="hidden"
      />

      {/* Upload button */}
      {showUploadButton && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || uploading}
          onClick={() => openPicker(null)}
          className="rounded-full mt-3"
          data-testid={`doc-upload-btn-${type.key}`}
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> {docs.length > 0 ? 'Upload another' : 'Upload'}</>
          )}
        </Button>
      )}
    </div>
  );
};

const VerificationDocuments = ({ profile }) => {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (profile) {
      facilitatorAPI.getMyDocuments()
        .then((res) => setDocs(res.data || []))
        .catch(() => setDocs([]));
    }
  }, [profile]);

  const handleUpload = (doc) => setDocs((prev) => [...prev, doc]);

  const handleRemove = async (id, opts = {}) => {
    if (opts.localOnly) {
      setDocs((prev) => prev.filter((d) => d.id !== id));
      return;
    }
    try {
      await facilitatorAPI.deleteDocument(id);
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      // leave list unchanged on failure
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-6" data-testid="verification-documents-section">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-semibold text-xl">Verification Documents</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        These documents are only visible to Campia admins and never shared publicly.
      </p>

      {!profile && (
        <div className="flex items-start gap-2 mb-5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="doc-save-first-note">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Save your profile first (use the Save button below) to enable document uploads.</span>
        </div>
      )}

      <div className="space-y-4">
        {DOC_TYPES.map((type) => (
          <UploadSlot
            key={type.key}
            type={type}
            docs={docs.filter((d) => d.type === type.key)}
            disabled={!profile}
            onUpload={handleUpload}
            onRemove={handleRemove}
          />
        ))}
      </div>
    </div>
  );
};

export default VerificationDocuments;
